/**
 * Offline quest completion queue.
 * Stores pending completions in IndexedDB when network is unavailable.
 * Syncs automatically when the device reconnects.
 *
 * DB: nq_offline / store: pendingCompletions
 */

const DB_NAME = 'nq_offline'
const DB_VERSION = 2
const STORE = 'pendingCompletions'
const QUEST_STORE = 'cachedChildQuests'
const MAX_SYNC_ATTEMPTS = 5

let _db = null

function openDB() {
  if (_db) return Promise.resolve(_db)
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (e) => {
      const db = e.target.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'localId' })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
      if (!db.objectStoreNames.contains(QUEST_STORE)) {
        db.createObjectStore(QUEST_STORE, { keyPath: 'id' })
      }
    }
    req.onsuccess = () => { _db = req.result; resolve(_db) }
    req.onerror = () => reject(req.error)
  })
}

function txStore(mode, storeName = STORE) {
  return openDB().then(db => db.transaction(storeName, mode).objectStore(storeName))
}

function promisify(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Queue a completion for later sync.
 * @param {object} data
 * @param {string} data.questId
 * @param {string|null} data.childId
 * @param {string|null} data.notes
 * @param {File[]} data.proofs
 * @param {boolean} data.shareWithGroup
 * @param {string|null} data.collaboratingFamilyId
 * @param {string} data.token  — auth token at time of submission
 */
export async function queueOfflineCompletion(data) {
  // Validate token is present
  if (!data.token) {
    throw new Error('Ingen giltig token — logga in igen.')
  }

  // Check storage quota before storing photos
  if (navigator.storage?.estimate) {
    const { quota, usage } = await navigator.storage.estimate()
    const needed = data.proofs.reduce((acc, f) => acc + f.size, 0)
    if (needed > (quota - usage) * 0.8) {
      throw new Error('Inte tillräckligt lagringsutrymme för att spara offline.')
    }
  }

  const record = {
    localId: crypto.randomUUID(),
    questId: data.questId,
    childId: data.childId || null,
    notes: data.notes || null,
    shareWithGroup: !!data.shareWithGroup,
    collaboratingFamilyId: data.collaboratingFamilyId || null,
    proofs: data.proofs.map(f => ({
      blob: f,
      kind: f.type.startsWith('video/') ? 'video' : 'image',
      filename: f.name,
    })),
    token: data.token,
    createdAt: Date.now(),
    syncAttempts: 0,
    lastError: null,
  }

  const store = await txStore('readwrite')
  await promisify(store.add(record))

  // Register background sync if supported (Chrome/Edge)
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready
      if ('sync' in reg) await reg.sync.register('sync-completions')
    } catch { /* not supported */ }
  }

  return record.localId
}

export async function getPendingCompletions() {
  const store = await txStore('readonly')
  return promisify(store.getAll())
}

export async function getPendingCount() {
  const all = await getPendingCompletions()
  return all.filter(c => !c.abandoned).length
}

export async function getAbandonedCount() {
  const all = await getPendingCompletions()
  return all.filter(c => c.abandoned).length
}

export async function deletePending(localId) {
  const store = await txStore('readwrite')
  return promisify(store.delete(localId))
}

async function updatePending(localId, updates) {
  const store = await txStore('readwrite')
  const record = await promisify(store.get(localId))
  if (record) await promisify(store.put({ ...record, ...updates }))
}

/**
 * Attempt to sync all pending completions.
 * Returns { synced, failed }.
 */
export async function syncPendingCompletions() {
  const all = await getPendingCompletions()
  // Skip abandoned completions — they need manual intervention (re-login etc.)
  const pending = all.filter(c => !c.abandoned)
  if (!pending.length) return { synced: 0, failed: 0 }

  let synced = 0, failed = 0

  for (const completion of pending) {
    try {
      const fd = new FormData()
      if (completion.childId) fd.append('childId', completion.childId)
      if (completion.notes) fd.append('notes', completion.notes)
      fd.append('shareWithGroup', completion.shareWithGroup ? 'true' : 'false')
      if (completion.collaboratingFamilyId) {
        fd.append('collaborating_family_id', completion.collaboratingFamilyId)
      }
      for (const proof of completion.proofs) {
        fd.append('proof', proof.blob, proof.filename)
      }

      const res = await fetch(`/api/quests/${completion.questId}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${completion.token}`,
        },
        body: fd,
      })

      if (res.ok || res.status === 400) {
        // 400 = already completed via another path — treat as success
        await deletePending(completion.localId)
        synced++
      } else if (res.status === 401) {
        // Token expired — can't recover without re-login, abandon immediately
        await updatePending(completion.localId, {
          lastError: 'auth_expired',
          abandoned: true,
        })
        failed++
      } else {
        const attempts = completion.syncAttempts + 1
        if (attempts >= MAX_SYNC_ATTEMPTS) {
          await updatePending(completion.localId, {
            syncAttempts: attempts,
            lastError: `HTTP ${res.status}`,
            abandoned: true,
          })
        } else {
          await updatePending(completion.localId, {
            syncAttempts: attempts,
            lastError: `HTTP ${res.status}`,
          })
        }
        failed++
      }
    } catch {
      // Network error — keep for retry unless max attempts reached
      const attempts = completion.syncAttempts + 1
      if (attempts >= MAX_SYNC_ATTEMPTS) {
        await updatePending(completion.localId, {
          syncAttempts: attempts,
          lastError: 'network',
          abandoned: true,
        })
      } else {
        await updatePending(completion.localId, {
          syncAttempts: attempts,
          lastError: 'network',
        })
      }
      failed++
    }
  }

  return { synced, failed }
}

// ── Child quest cache ──────────────────────────────────────────

/**
 * Cache child-independent quests (requires_parent = false) for offline use.
 * @param {Array} quests
 */
export async function cacheChildQuests(quests) {
  const db = await openDB()
  const tx = db.transaction(QUEST_STORE, 'readwrite')
  const store = tx.objectStore(QUEST_STORE)
  // Clear old cache first
  store.clear()
  for (const quest of quests) {
    store.put(quest)
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = resolve
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * Get cached child quests, optionally filtered by child's age.
 * @param {number|null} birthYear
 * @param {number|null} birthMonth
 */
export async function getCachedChildQuests(birthYear = null, birthMonth = null) {
  const store = await txStore('readonly', QUEST_STORE)
  const all = await promisify(store.getAll())

  if (!birthYear) return all

  // Calculate age in years
  const now = new Date()
  const birth = new Date(birthYear, (birthMonth || 1) - 1, 1)
  const ageYears = Math.floor((now - birth) / (365.25 * 24 * 60 * 60 * 1000))

  return all.filter(q => ageYears >= (q.min_age || 0) && ageYears <= (q.max_age || 99))
}

/**
 * Clear all offline data — call on logout.
 * Removes pending completions (with stored tokens + photos) and cached quests.
 */
export async function clearOfflineData() {
  try {
    await new Promise((resolve, reject) => {
      const req = indexedDB.deleteDatabase(DB_NAME)
      req.onsuccess = resolve
      req.onerror = () => reject(req.error)
      req.onblocked = resolve // proceed even if blocked
    })
    _db = null // reset cached connection
  } catch {
    // Non-fatal — best effort
  }
}
